import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useMediaQuery from '@mui/material/useMediaQuery';

import variables from 'src/style/variable/variables.module.scss';

export default function ScrollToBottomContainer ({
  children,
  isCollapsed = false,
  buttonOffset = 10
}) {
  const [showExpandButton, setShowExpandButton] = useState(true);
  const [buttonPosition, setButtonPosition] = useState({ left: '50%' });
  const containerRef = useRef(null);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current && !isCollapsed) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Calculate button position centered on the container
        const containerCenter = containerRect.left + (containerRect.width / 2);
        setButtonPosition({ left: `${containerCenter}px` });

        // If the container bottom is above the viewport bottom, hide the button
        if (containerRect.bottom - buttonOffset <= viewportHeight) {
          setShowExpandButton(false);
        } else {
          setShowExpandButton(true);
        }
      } else {
        // Show button when section is collapsed or ref not available
        setShowExpandButton(true);
      }
    };

    const onScroll = () => {
      handleScroll();
    };

    // Listen to window scroll (most common)
    window.addEventListener('scroll', onScroll, { passive: true });

    // Listen to window resize to update button position when layout changes
    window.addEventListener('resize', onScroll, { passive: true });

    // Also try document scroll
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });

    // Check if there's a main content container that scrolls
    const mainContent = document.querySelector('.page') || document.querySelector('main') || document.querySelector('#root');
    if (mainContent) {
      mainContent.addEventListener('scroll', onScroll, { passive: true });
    }

    // Check initial position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (mainContent) {
        mainContent.removeEventListener('scroll', onScroll);
      }
    };
  }, [isCollapsed, buttonOffset]);

  const handleExpandClick = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'end'
      });
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div ref={containerRef}>
        {children}
      </div>

      {!isCollapsed && showExpandButton && (
        <div
          style={{
            position: 'fixed',
            left: buttonPosition.left,
            bottom: (isMobile && isPortrait) ? '70px' : '20px',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            zIndex: 1000
          }}
          onClick={handleExpandClick}
        >
          <ExpandMoreIcon style={{
            fontSize: '32px'
          }} />
        </div>
      )}
    </div>
  );
}

ScrollToBottomContainer.propTypes = {
  children: PropTypes.node.isRequired,
  isCollapsed: PropTypes.bool,
  buttonOffset: PropTypes.number
};
